/**
 * MessageTranslation — provides per-message translation state & UI.
 *
 * Architecture:
 *   TranslationProvider  – React context wrapping the entire Channel; holds a
 *                          Map<messageId, { text, lang }> of cached translations
 *                          and the async `translate(messageId, text)` action.
 *
 *   TranslatedBubble     – Rendered below a message's text when a translation
 *                          exists. Shows translated text, "Google Translate"
 *                          attribution, and a "Hide Translation" button.
 *
 *   useMessageTranslation – Hook consumed by the CustomMessage wrapper.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useChatContext } from "stream-chat-react";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  type LanguageCode,
} from "@/features/chat/services/translationService";

// ─── Types ───

interface TranslationEntry {
  text: string;
  targetLang: string;
  sourceLang?: string;
  /** Original source text that was translated — used to detect edited messages */
  sourceText: string;
}

interface TranslationContextType {
  /** Cached translations keyed by message ID */
  translations: Record<string, TranslationEntry>;
  /** Set of message IDs currently being translated */
  loadingIds: Set<string>;
  /** Set of message IDs whose translations are visible */
  visibleIds: Set<string>;
  /** Trigger a translation for a message */
  translateMessage: (messageId: string, text: string) => Promise<void>;
  /** Hydrate a translation from Stream message custom fields (no API call) */
  addTranslationFromStreamCache: (messageId: string, entry: TranslationEntry) => void;
  /** Toggle translation visibility (show/hide) */
  toggleVisibility: (messageId: string) => void;
  /** Hide a specific translation */
  hideTranslation: (messageId: string) => void;
  /** Whether auto-translate is enabled for this user */
  autoTranslateEnabled: boolean;
  /** Language codes that should NOT be auto-translated */
  translateExceptions: string[];
}

const TranslationContext = createContext<TranslationContextType>({
  translations: {},
  loadingIds: new Set(),
  visibleIds: new Set(),
  translateMessage: async () => {},
  addTranslationFromStreamCache: () => {},
  toggleVisibility: () => {},
  hideTranslation: () => {},
  autoTranslateEnabled: false,
  translateExceptions: [],
});

export const useMessageTranslation = () => useContext(TranslationContext);

// ─── Module-level queue — limits concurrent API calls to avoid flooding ───

const _inFlight = new Set<string>();
const _queue: Array<() => void> = [];
let _active = 0;
const MAX_CONCURRENT = 3;

function _drainQueue(): void {
  while (_active < MAX_CONCURRENT && _queue.length > 0) {
    _queue.shift()!();
  }
}

function _scheduleTranslation(messageId: string, task: () => Promise<void>): void {
  if (_inFlight.has(messageId)) return;
  _inFlight.add(messageId);
  _queue.push(() => {
    _active++;
    task().finally(() => {
      _active--;
      _inFlight.delete(messageId);
      _drainQueue();
    });
  });
  _drainQueue();
}

// ─── Provider ───

export const TranslationProvider = ({
  children,
  autoTranslateEnabled = false,
  translateExceptions = [],
}: {
  children: React.ReactNode;
  autoTranslateEnabled?: boolean;
  translateExceptions?: string[];
}) => {
  const { i18n } = useTranslation();
  const { client } = useChatContext();

  const [translations, setTranslations] = useState<
    Record<string, TranslationEntry>
  >({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());

  const getTargetLang = useCallback((): LanguageCode => {
    // Priority: i18n current language → fallback "en"
    const lang = i18n.language?.split("-")[0] || "en";
    const supported = SUPPORTED_LANGUAGES.map((l) => l.code as string);
    return (supported.includes(lang) ? lang : "en") as LanguageCode;
  }, [i18n.language]);

  const translateMessage = useCallback(
    (messageId: string, text: string): Promise<void> => {
      if (!text?.trim() || !messageId) return Promise.resolve();

      const targetLang = getTargetLang();

      // ── Check 1: local cache (synchronous — no network) ──
      const cached = translations[messageId];
      if (cached && cached.targetLang === targetLang && cached.sourceText === text) {
        // Only show bubble if there is actual translated text (empty = same-language skip)
        if (cached.text) setVisibleIds((prev) => new Set(prev).add(messageId));
        return Promise.resolve();
      }

      // ── Schedule remaining work through the concurrency queue ──
      // Prevents flooding when a large chat loads with many messages at once.
      _scheduleTranslation(messageId, async () => {
        // ── Check 2: Stream message custom data (cross-user cache) ──
        try {
          const response = await client.getMessage(messageId);
          const msg = response?.message;
          const cacheKey = `translations_${targetLang}`;
          const serverCached = (msg as any)?.[cacheKey];
          const serverOriginal = (msg as any)?.[`${cacheKey}_original`];

          if (serverCached && typeof serverCached === "string" && serverOriginal === text) {
            const sourceLang = (msg as any)?.[`translations_${targetLang}_source`];
            // Respect exception list even for cached results
            if (sourceLang && translateExceptions.includes(sourceLang)) return;
            const entry: TranslationEntry = {
              text: serverCached,
              targetLang,
              sourceLang,
              sourceText: text,
            };
            setTranslations((prev) => ({ ...prev, [messageId]: entry }));
            setVisibleIds((prev) => new Set(prev).add(messageId));
            return;
          }
        } catch {
          // Stream cache miss — fall through to Google Translate
        }

        // ── Call Google Translate API ──
        setLoadingIds((prev) => new Set(prev).add(messageId));

        try {
          const result = await translateText(text, targetLang);

          if (result.success && result.translatedText) {
            // Skip if message is already in the user's language — cache empty marker to prevent re-calls
            if (result.detectedSourceLanguage === targetLang) {
              setTranslations((prev) => ({
                ...prev,
                [messageId]: { text: "", targetLang, sourceLang: targetLang, sourceText: text },
              }));
              return;
            }

            // Skip if source language is in the user's exception list
            if (result.detectedSourceLanguage && translateExceptions.includes(result.detectedSourceLanguage)) {
              setTranslations((prev) => ({
                ...prev,
                [messageId]: { text: "", targetLang, sourceLang: result.detectedSourceLanguage, sourceText: text },
              }));
              return;
            }

            const entry: TranslationEntry = {
              text: result.translatedText,
              targetLang,
              sourceLang: result.detectedSourceLanguage,
              sourceText: text,
            };

            setTranslations((prev) => ({ ...prev, [messageId]: entry }));
            setVisibleIds((prev) => new Set(prev).add(messageId));

            // ── Persist to Stream message (cross-user cache) ──
            try {
              const cacheKey = `translations_${targetLang}`;
              await client.partialUpdateMessage(messageId, {
                set: {
                  [cacheKey]: result.translatedText,
                  [`${cacheKey}_source`]: result.detectedSourceLanguage || "auto",
                  [`${cacheKey}_original`]: text,
                },
              });
            } catch {
              // Cache persistence is best-effort
            }
          }
        } catch (err) {
          console.error("[Translate] API call failed:", err);
        } finally {
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
        }
      });

      return Promise.resolve();
    },
    [client, getTargetLang, translations],
  );

  const addTranslationFromStreamCache = useCallback(
    (messageId: string, entry: TranslationEntry) => {
      setTranslations((prev) => {
        // Skip if we already have an up-to-date entry
        const existing = prev[messageId];
        if (existing && existing.targetLang === entry.targetLang && existing.sourceText === entry.sourceText) {
          return prev;
        }
        return { ...prev, [messageId]: entry };
      });
      setVisibleIds((prev) => new Set(prev).add(messageId));
    },
    [],
  );

  // Listen for message.updated events fired when the backend webhook stores
  // translations via partialUpdateMessage. This handles the race condition where
  // the webhook finishes after the message has already rendered on the client.
  useEffect(() => {
    if (!autoTranslateEnabled || !client) return;

    const { unsubscribe } = client.on("message.updated", (event) => {
      const msg = event.message;
      if (!msg?.id || !msg?.text) return;
      if (msg.user?.id === client.userID) return; // own messages

      const targetLang = getTargetLang();
      const cacheKey = `translations_${targetLang}`;
      const stored = (msg as any)[cacheKey];
      const storedOriginal = (msg as any)[`${cacheKey}_original`];

      if (stored && storedOriginal === msg.text) {
        const sourceLang = (msg as any)[`${cacheKey}_source`];
        if (sourceLang && translateExceptions.includes(sourceLang)) return;
        addTranslationFromStreamCache(msg.id, {
          text: stored,
          targetLang,
          sourceLang,
          sourceText: msg.text,
        });
      }
    });

    return unsubscribe;
  }, [autoTranslateEnabled, client, getTargetLang, addTranslationFromStreamCache, translateExceptions]);

  const toggleVisibility = useCallback((messageId: string) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  const hideTranslation = useCallback((messageId: string) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      next.delete(messageId);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      translations,
      loadingIds,
      visibleIds,
      translateMessage,
      addTranslationFromStreamCache,
      toggleVisibility,
      hideTranslation,
      autoTranslateEnabled,
      translateExceptions,
    }),
    [
      translations,
      loadingIds,
      visibleIds,
      translateMessage,
      addTranslationFromStreamCache,
      toggleVisibility,
      hideTranslation,
      autoTranslateEnabled,
      translateExceptions,
    ],
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// ─── Inline Translation Display ───

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  ja: "Japanese",
  vi: "Vietnamese",
  id: "Indonesian",
  tl: "Tagalog",
  bn: "Bengali",
  km: "Khmer",
  my: "Burmese",
  ne: "Nepali",
  th: "Thai",
};

export const TranslatedBubble = ({
  messageId,
  currentText,
}: {
  messageId: string;
  currentText?: string;
}) => {
  const { t } = useTranslation();
  const { translations, loadingIds, visibleIds, hideTranslation } =
    useMessageTranslation();

  const isLoading = loadingIds.has(messageId);
  const isVisible = visibleIds.has(messageId);
  const entry = translations[messageId];

  // Auto-hide translation when the message has been edited since last translation
  const isStale = Boolean(currentText && entry && entry.sourceText !== currentText);
  useEffect(() => {
    if (isStale) {
      hideTranslation(messageId);
    }
  }, [isStale, messageId, hideTranslation]);

  if (isLoading) {
    return (
      <div className="mt-1.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <svg
            className="w-3.5 h-3.5 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span>{t("chat.translating")}</span>
        </div>
      </div>
    );
  }

  if (!isVisible || !entry || isStale) return null;

  return (
    <div className="mt-1.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.text}</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-gray-400">
          AI Translate
          {entry.sourceLang && entry.targetLang && (
            <>
              {" "}
              &middot; {LANGUAGE_NAMES[entry.sourceLang] || entry.sourceLang}{" "}
              &rarr; {LANGUAGE_NAMES[entry.targetLang] || entry.targetLang}
            </>
          )}
        </span>
        <button
          onClick={() => hideTranslation(messageId)}
          className="text-[10px] text-blue-500 hover:text-blue-700 font-medium"
        >
          {t("chat.hide_translation")}
        </button>
      </div>
    </div>
  );
};

// ─── Globe icon for translate action ───

export const GlobeIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-4.247m0 0A8.966 8.966 0 013 12c0-1.257.26-2.454.726-3.541"
    />
  </svg>
);
